import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { MarqueeContainer } from './PhotoMarquee.styles';
import type { PhotoMarqueeProps } from './PhotoMarquee.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { LOW_RES_RENDITION, OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';
import { MAX_ASPECT_RATIO, PHOTO_HEIGHT, MAX_HEIGHT } from './PhotoMarquee.constants';
import { MarqueePhoto } from './MarqueePhoto';

const PHOTO_COUNT_TO_SHOW = 6;

export const PhotoMarquee: FC<PhotoMarqueeProps> = memo(props => {
  const { photos, showSkeleton, showPlaceholder, isPageWidth = false } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);
  const marqueePhotos = useMemo(() => [...filledPhotos, ...filledPhotos], [filledPhotos]);

  const [containerDimensions, containerRef] = useSizeObserver();
  // Visible width of the marquee (the overflow-hidden viewport = page column when page width).
  // The inner MarqueeContainer measures the full scrolling content, so it can't give us this.
  const [viewportDimensions, viewportRef] = useSizeObserver();

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  // Page width: match the full-width height:width ratio by capping height off the visible width
  // instead of 100vw. Falls back to MAX_HEIGHT until first measured.
  const maxHeight = isPageWidth && viewportDimensions.width ? viewportDimensions.width / MAX_ASPECT_RATIO : MAX_HEIGHT;
  const placeholderWidthBase = isPageWidth && viewportDimensions.width ? `${viewportDimensions.width}px` : '100vw';

  return (
    <Flex
      ref={viewportRef}
      width={isPageWidth ? '100%' : '100vw'}
      position="relative"
      left={isPageWidth ? '0' : 'calc(-50vw + 50%)'}
      overflow="hidden"
    >
      <MarqueeContainer
        flexDirection="row"
        flexBasis="100%"
        gap={1}
        maxHeight={maxHeight}
        height={PHOTO_HEIGHT}
        ref={containerRef}
      >
        {marqueePhotos.map((photo, index) => (
          <SsrLazyScreen
            key={`${photo.id}-${index}`}
            src={addRendition({ url: photo.photoUrl, renditionSize: LOW_RES_RENDITION })}
            width={`calc(calc(${placeholderWidthBase} / ${MAX_ASPECT_RATIO}) * ${photo.width / photo.height})`}
            height="100%"
            showSkeleton={showSkeleton}
          >
            {() =>
              photo.photoUrl ? (
                <MarqueePhoto
                  photo={photo}
                  index={index}
                  containerDimensions={containerDimensions}
                  photoRenderer={OptimizedEventGuestSitePhoto}
                />
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  aspectRatio={`${MAX_ASPECT_RATIO}`}
                  backgroundColor="lightgray"
                  key={photo.id}
                />
              )
            }
          </SsrLazyScreen>
        ))}
      </MarqueeContainer>
    </Flex>
  );
});

PhotoMarquee.displayName = 'PhotoMarquee';
